<template>
  <div class="word-search">
    <section class="hero">
      <h1 class="hero-title">查单词</h1>
      <p class="hero-desc">{{ searchDirection === 'zh2en' ? '输入中文，翻译成英文' : '输入英文，获取释义与例句' }}</p>
      <div class="direction-tabs">
        <button
          :class="['tab-btn', { active: searchDirection === 'en2zh' }]"
          @click="searchDirection = 'en2zh'"
        >英翻中</button>
        <button
          :class="['tab-btn', { active: searchDirection === 'zh2en' }]"
          @click="searchDirection = 'zh2en'"
        >中翻英</button>
      </div>
      <div class="search-hero">
        <input
          v-model="searchQuery"
          @keyup.enter="handleSearch"
          type="text"
          :placeholder="searchDirection === 'zh2en' ? '输入中文，按回车查询...' : '输入单词，按回车查询...'"
          class="search-input"
        >
        <button @click="handleSearch" :disabled="searchLoading" class="btn-search">
          <span v-if="searchLoading" class="loading-spinner"></span>
          <span v-else>查询</span>
        </button>
      </div>
    </section>

    <section class="section">
      <div class="section-head">
        <h2 class="section-title">最近查询</h2>
        <button v-if="allWords.length > 0" @click="clearAllHistory" class="btn-ghost">清空</button>
      </div>
      <div v-if="loading" class="state-msg">加载中...</div>
      <div v-else-if="allWords.length === 0" class="state-msg empty">暂无历史记录，查一个试试</div>
      <div v-else class="word-grid">
        <div v-for="word in allWords" :key="word.wordName" class="word-card">
          <div class="word-header">
            <h4 class="word-title">{{ word.wordName }}</h4>
            <span class="history-time" v-if="word.updateTime">{{ new Date(word.updateTime).toLocaleDateString() }}</span>
          </div>
          <!-- 显示音标 -->
          <div class="word-phonetic" v-if="(word.ukphone && word.ukphone.trim()) || (word.usphone && word.usphone.trim())">
            <span v-if="word.ukphone && word.ukphone.trim()" class="phonetic-item">UK: {{ word.ukphone }}</span>
            <span v-if="word.usphone && word.usphone.trim()" class="phonetic-item">US: {{ word.usphone }}</span>
          </div>
          <div class="word-meaning">
            <template v-if="word.parsedMeaning">
              <span class="word-pos">({{ word.parsedMeaning.pos }})</span>
              <span class="word-trans">{{ word.parsedMeaning.tran_cn }}</span>
            </template>
            <template v-else>
              {{ word.wordMeaning }}
            </template>
          </div>
          <div class="word-actions">
            <button @click="showDetail(word.wordName)" class="btn-detail">
              详情
            </button>
            <button @click="addToBook(word)" class="btn-add">
              + 加入
            </button>
            <button @click="deleteHistoryItem(word.wordName)" class="btn-delete">
              删除
            </button>
          </div>
        </div>
      </div>
      <div v-if="total > pageSize" class="pagination">
        <button :disabled="pageNo === 1" @click="handlePageChange(pageNo - 1)" class="page-btn">上一页</button>
        <span class="page-info">{{ pageNo }} / {{ Math.ceil(total / pageSize) }}</span>
        <button :disabled="pageNo * pageSize >= total" @click="handlePageChange(pageNo + 1)" class="page-btn">下一页</button>
      </div>
    </section>
    
    <!-- 单词详情弹窗 -->
    <WordDetailModal
      :visible.sync="showDetailModal"
      :word-name="detailWordName"
      :direction="searchDirection"
    />

    <div v-if="showAddModal" class="overlay" @click.self="closeModal">
      <div class="panel">
        <div class="panel-head">
          <h3>添加到单词本</h3>
          <button type="button" @click="closeModal" class="btn-close" aria-label="关闭">×</button>
        </div>
        <div class="panel-body">
          <p class="panel-desc">选择要添加到的单词本</p>
          <div class="book-select-list">
            <div 
              v-for="book in wordBooks" 
              :key="book" 
              class="book-option"
              @click="confirmAddToBook(book)"
            >
              {{ book }}
            </div>
            <div v-if="wordBooks.length === 0" class="empty-books">
              暂无单词本，请先去创建
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAllWords, getWordByName, addHistory, deleteHistory, clearHistory } from '@/api/word'
import { getWordBooks, addWordToBook } from '@/api/wordBook'
import WordDetailModal from './WordDetailModal.vue'

export default {
  name: 'WordSearch',
  components: {
    WordDetailModal
  },
  data() {
    return {
      searchQuery: '',
      searchDirection: 'en2zh', // en2zh 英翻中 | zh2en 中翻英
      searchResult: [],
      allWords: [],
      loading: false,
      searchLoading: false,
      showAddModal: false,
      selectedWord: null,
      wordBooks: [],
      userName: localStorage.getItem('userName'),
      showDetailModal: false,
      detailWordName: '',
      pageNo: 1,
      pageSize: 12,
      total: 0
    }
  },
  mounted() {
    this.fetchAllWords()
  },
  methods: {
    async fetchAllWords() {
      this.loading = true
      try {
        const res = await getAllWords(this.userName, this.pageNo, this.pageSize)
        if (res.data.success) {
          const data = res.data.data
          if (data && data.items) {
            this.allWords = data.items.map(word => ({
              ...word,
              parsedMeaning: this.parseWordMeaning(word.wordMeaning)
            }))
            this.total = data.total
          } else {
            this.allWords = []
            this.total = 0
          }
        }
      } catch (error) {
        console.error('获取历史记录失败', error)
      } finally {
        this.loading = false
      }
    },
    async handleSearch() {
      if (!this.searchQuery.trim()) {
        this.searchResult = []
        return
      }
      
      // 设置搜索加载状态
      this.searchLoading = true
      
      try {
        const res = await getWordByName(this.searchQuery, this.searchDirection)
        if (res.data.success && res.data.data) {
          const data = res.data.data
          const displayWordName = data.wordName || this.searchQuery
          const parsedMeaning = this.parseWordMeaning(data.wordMeaning)
          const searchResult = [{
            wordName: displayWordName,
            wordMeaning: data.wordMeaning,
            parsedMeaning: parsedMeaning,
            ukphone: data.ukphone,
            usphone: data.usphone,
            updateTime: new Date().toISOString()
          }]
          this.searchResult = searchResult
          try {
            await addHistory(this.userName, displayWordName)
            const existsIndex = this.allWords.findIndex(word => word.wordName === displayWordName)
            if (existsIndex !== -1) {
              // 更新现有单词的时间戳和内容
              this.allWords.splice(existsIndex, 1)
              this.allWords.unshift(searchResult[0])
            } else {
              // 添加新单词到历史记录顶部
              this.allWords.unshift(searchResult[0])
              this.total += 1
            }
          } catch (historyError) {
            console.error('添加到历史记录失败', historyError)
            // 历史记录添加失败不影响搜索结果显示
          }
        } else {
          this.searchResult = []
          this.$message.warning('未找到该单词')
        }
      } catch (error) {
        console.error('查询单词失败', error)
        this.searchResult = []
        if (this.$message) {
          this.$message.error('查询单词失败')
        }
      } finally {
        // 无论成功失败，都关闭加载状态
        this.searchLoading = false
      }
    },
    async deleteHistoryItem(wordName) {
      try {
        const res = await deleteHistory(this.userName, wordName)
        if (res.data.success) {
          this.$message.success('删除成功')
          this.fetchAllWords()
        }
      } catch (error) {
        console.error('删除历史记录失败', error)
        this.$message.error('删除失败')
      }
    },
    async clearAllHistory() {
      if (!confirm('确定要清空所有历史记录吗？此操作不可恢复！')) return
      try {
        const res = await clearHistory(this.userName)
        if (res.data.success) {
          this.$message.success('清空成功')
          this.fetchAllWords()
        }
      } catch (error) {
        console.error('清空历史记录失败', error)
        this.$message.error('清空失败')
      }
    },
    handlePageChange(page) {
      this.pageNo = page
      this.fetchAllWords()
    },
    // 安全解析JSON字符串
    parseWordMeaning(meaning) {
      if (!meaning) return null
      
      try {
        // 检查是否已经是JSON字符串格式
        if (typeof meaning === 'string' && meaning.startsWith('{')) {
          const parsed = JSON.parse(meaning)
          // 只有当解析后的对象包含pos和tran_cn属性时才返回该对象
          if (parsed && typeof parsed === 'object' && parsed.pos && parsed.tran_cn) {
            return parsed
          }
        }
        // 对于其他情况（数组、普通字符串、不包含必要属性的对象），返回null
        return null
      } catch (error) {
        console.error('解析wordMeaning失败', error)
        return null
      }
    },
    async addToBook(word) {
      this.selectedWord = word
      try {
        const res = await getWordBooks(this.userName)
        if (res.data.success) {
          this.wordBooks = res.data.data
          this.showAddModal = true
        }
      } catch (error) {
        console.error('获取单词本失败', error)
        this.$message.error('获取单词本列表失败')
      }
    },
    async confirmAddToBook(bookName) {
      if (!this.selectedWord) return
      
      try {
        const wordData = {
          wordName: this.selectedWord.wordName,
          wordMeaning: this.selectedWord.wordMeaning,
          ukphone: this.selectedWord.ukphone,
          usphone: this.selectedWord.usphone
        }
        const res = await addWordToBook(this.userName, bookName, wordData)
        if (res.data.success) {
          this.$message.success('加入成功')
          this.closeModal()
        } else {
          this.$message.error('添加失败: ' + res.data.errMsg)
        }
      } catch (error) {
        console.error('添加失败', error)
        this.$message.error('添加失败')
      }
    },
    closeModal() {
      this.showAddModal = false
      this.selectedWord = null
    },
    showDetail(wordName) {
      this.detailWordName = wordName
      this.showDetailModal = true
    }
  }
}
</script>

<style scoped>
.word-search {
  padding: 0 0 64px;
}

.hero {
  padding: 64px 24px 56px;
  text-align: center;
  max-width: 720px;
  margin: 0 auto;
}

.hero-title {
  font-family: var(--font-display);
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 800;
  letter-spacing: -0.04em;
  color: var(--ink);
  margin-bottom: 8px;
  line-height: 1.1;
}

.hero-desc {
  font-size: 1.1rem;
  color: var(--ink-muted);
  margin-bottom: 16px;
}

.direction-tabs {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
}

.tab-btn {
  padding: 8px 20px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-card);
  color: var(--ink-muted);
  font-size: 0.95rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s, background 0.2s;
}

.tab-btn:hover {
  border-color: var(--border-strong);
  color: var(--ink);
}

.tab-btn.active {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--accent-soft);
}

.search-hero {
  display: flex;
  gap: 12px;
  background: var(--surface-card);
  padding: 8px 8px 8px 24px;
  border-radius: var(--radius-xl);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  transition: box-shadow 0.2s, border-color 0.2s;
}

.search-hero:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 4px var(--accent-soft);
}

.search-input {
  flex: 1;
  min-width: 0;
  padding: 16px 0;
  border: none;
  font-size: 1.1rem;
  outline: none;
  color: var(--ink);
  background: transparent;
}

.search-input::placeholder {
  color: var(--ink-muted);
}

.btn-search {
  padding: 0 32px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: var(--radius-lg);
  font-size: 1rem;
  font-weight: 600;
  transition: background 0.2s;
}

.btn-search:disabled { opacity: 0.7; cursor: not-allowed; }
.btn-search:hover:not(:disabled) { background: var(--accent-hover); }

.loading-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
}

@keyframes spin { to { transform: rotate(360deg); } }

.section {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
}

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--ink);
  margin: 0;
  letter-spacing: -0.02em;
}

.btn-ghost {
  padding: 8px 14px;
  border-radius: var(--radius);
  background: transparent;
  color: var(--ink-muted);
  border: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: color 0.2s, background 0.2s;
}

.btn-ghost:hover {
  color: var(--danger);
  background: rgba(239, 68, 68, 0.08);
}

.state-msg {
  text-align: center;
  padding: 48px 20px;
  color: var(--ink-muted);
  font-size: 1rem;
}

.state-msg.empty {
  padding: 64px 20px;
  font-size: 1.05rem;
}

.word-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  padding-bottom: 40px;
}

.word-card {
  background: var(--surface-card);
  padding: 24px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  transition: border-color 0.2s, box-shadow 0.2s;
  animation: fadeIn 0.3s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.word-card:hover {
  border-color: var(--border-strong);
  box-shadow: var(--shadow-md);
}

.word-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.word-title {
  font-size: 1.3rem;
  color: var(--ink);
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.history-time {
  font-size: 0.8rem;
  color: var(--ink-muted);
  font-weight: 500;
}

.word-phonetic {
  color: var(--ink-secondary);
  font-size: 0.85rem;
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.phonetic-item {
  background: var(--surface);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
}

.word-meaning {
  color: var(--ink-secondary);
  margin-bottom: 20px;
  line-height: 1.5;
  flex: 1;
  font-size: 0.95rem;
}

.word-pos {
  color: var(--secondary-color);
  font-style: italic;
  margin-right: 6px;
  font-weight: 600;
  font-size: 0.85rem;
}

.word-trans { color: var(--ink); }

.word-actions {
  display: flex;
  gap: 10px;
  margin-top: auto;
}

.btn-detail, .btn-add, .btn-delete {
  flex: 1;
  padding: 10px 14px;
  border-radius: var(--radius);
  font-size: 0.88rem;
  font-weight: 600;
  text-align: center;
  border: none;
  transition: background 0.2s, color 0.2s;
}

.btn-detail {
  background: var(--surface);
  color: var(--ink-secondary);
  border: 1px solid var(--border);
}

.btn-detail:hover {
  background: var(--border);
  color: var(--ink);
}

.btn-add {
  background: var(--accent);
  color: #fff;
}

.btn-add:hover { background: var(--accent-hover); }

.btn-delete {
  background: transparent;
  color: var(--ink-muted);
  border: 1px solid var(--border);
}

.btn-delete:hover {
  color: var(--danger);
  border-color: rgba(239, 68, 68, 0.4);
}

.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
}

.panel {
  background: var(--surface-card);
  width: 90%;
  max-width: 400px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.panel-head {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-head h3 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--ink);
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--ink-muted);
  width: 36px;
  height: 36px;
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.2s, background 0.2s;
  line-height: 1;
}

.btn-close:hover {
  background: var(--surface);
  color: var(--danger);
}

.panel-body {
  padding: 24px;
}

.panel-desc {
  color: var(--ink-secondary);
  font-size: 0.9rem;
  margin-bottom: 16px;
}

.book-select-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.book-option {
  padding: 14px 16px;
  background: var(--surface);
  border-radius: var(--radius);
  cursor: pointer;
  text-align: left;
  border: 1px solid transparent;
  font-weight: 500;
  color: var(--ink);
  font-size: 0.95rem;
  transition: all 0.2s;
}

.book-option:hover {
  border-color: var(--accent);
  background: var(--accent-soft);
}

.empty-books {
  color: var(--ink-muted);
  font-size: 0.9rem;
  padding: 12px 0;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 32px;
  gap: 16px;
}

.page-btn {
  padding: 8px 18px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--surface-card);
  color: var(--ink);
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-btn:not(:disabled):hover {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--accent-soft);
}

.page-info {
  color: var(--ink-muted);
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .hero { padding: 40px 20px 40px; }
  .search-hero { padding: 6px 6px 6px 16px; }
  .btn-search { padding: 0 24px; }
  .word-grid { grid-template-columns: 1fr; }
}
</style>